import { useEffect } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { Link } from 'react-router-dom';

const STSCylinderPage = () => {
  useEffect(() => {
    document.title = '삼원테크 | STS원통형 물탱크 - 3톤~500톤 일체형 스테인리스 물탱크';
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      '삼원테크 STS원통형 물탱크. 3톤~500톤 일체형 스테인리스 물탱크. STS304 재질, 7단계 제작 공정, 영구적 수밀성 보장. 아파트·병원·학교 등 다양한 시설에 적용.'
    );
    const schema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "STS원통형 물탱크",
      "description": "일체형 구조로 내구성이 뛰어나며, 중소규모 저수 시설에 적합한 스테인리스 물탱크. 3톤~500톤.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/process/sts-cylinder`,
      "brand": { "@type": "Brand", "name": "삼원테크" },
      "manufacturer": {
        "@type": "Organization",
        "name": "삼원테크",
        "url": import.meta.env.VITE_SITE_URL || 'https://example.com'
      },
      "material": "STS304 스테인리스 스틸",
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "priceCurrency": "KRW",
        "seller": { "@type": "Organization", "name": "삼원테크" }
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "홈", "item": import.meta.env.VITE_SITE_URL || 'https://example.com' },
          { "@type": "ListItem", "position": 2, "name": "제작과정", "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/process` },
          { "@type": "ListItem", "position": 3, "name": "STS원통형 물탱크", "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/process/sts-cylinder` }
        ]
      }
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-sts-cylinder';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById('schema-sts-cylinder')?.remove(); };
  }, []);

  const advantages = [
    {
      icon: 'ri-building-4-line',
      title: '구조적 안정성',
      description: '원통형 구조로 압력 분산이 균일하여 구조적으로 매우 안정적입니다'
    },
    {
      icon: 'ri-shield-check-line',
      title: '영구적 수밀성 보장',
      description: '일체형 용접 구조로 누수 위험이 없고 영구적인 수밀성을 보장합니다'
    },
    {
      icon: 'ri-tools-line',
      title: '청소, 유지관리 용이',
      description: '내부 구조가 단순하여 청소와 유지관리가 매우 편리합니다'
    },
    {
      icon: 'ri-water-flash-line',
      title: '청결, 내구성',
      description: 'STS 소재로 녹이 슬지 않고 위생적이며 반영구적으로 사용 가능합니다'
    },
    {
      icon: 'ri-leaf-line',
      title: '환경 친화적',
      description: '100% 재활용 가능한 스테인리스 소재로 환경을 보호합니다'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: '기초/내장형 배관 설치',
      description: '물탱크 설치를 위한 기초 작업과 내부 배관을 설치합니다.',
      icon: 'ri-hammer-line',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/15960d41c8ee3354ce07063679b32133.png'
    },
    {
      step: '02',
      title: '바닥판 설치',
      description: '정밀하게 제작된 바닥판을 기초 위에 안착시킵니다.',
      icon: 'ri-layout-bottom-line',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/ddb28b675999687e4dd47c6af552cb71.png'
    },
    {
      step: '03',
      title: '단계별 시공(1단)',
      description: '첫 번째 단의 원통형 몸체를 설치하고 용접합니다.',
      icon: 'ri-stack-line',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/dd0ce690dc71121dad07b469f0ce2ea7.png'
    },
    {
      step: '04',
      title: '단계별 시공(2단)',
      description: '두 번째 단의 원통형 몸체를 설치하고 용접합니다.',
      icon: 'ri-stack-fill',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/a5e4806efeda8bdee90542828eb7e603.png'
    },
    {
      step: '05',
      title: '아티론 보온재 설치',
      description: '단열 효과를 위한 아티론 보온재를 외부에 설치합니다.',
      icon: 'ri-temp-cold-line',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/1e8bcb081217527596ba9d1cccf82767.png'
    },
    {
      step: '06',
      title: '알루미늄 마감재 및 외부사다리/핸드레일 설치',
      description: '보온재 보호와 미관을 위한 알루미늄 마감재를 설치하고, 안전한 점검을 위한 외부사다리와 핸드레일을 함께 설치합니다.',
      icon: 'ri-paint-brush-line',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/46eb71372deac6f879baa7881b6be2b8.png'
    },
    {
      step: '07',
      title: '물탱크 제작완료',
      description: '최종 검사 및 시운전을 거쳐 물탱크 제작을 완료합니다.',
      icon: 'ri-checkbox-circle-line',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/851fac60f20398148d100f5a63c3d95f.png'
    }
  ];

  const features = [
    {
      icon: 'ri-shape-line',
      title: '일체형 구조',
      description: '이음새가 적어 누수 위험이 낮고 내구성이 뛰어납니다'
    },
    {
      icon: 'ri-shield-star-line',
      title: '고강도',
      description: '원통형 구조로 압력에 강하고 안정적입니다'
    },
    {
      icon: 'ri-time-line',
      title: '빠른 제작',
      description: '일체형 제작으로 공기가 짧습니다'
    },
    {
      icon: 'ri-water-flash-line',
      title: '다양한 용량',
      description: '0.5톤부터 100톤까지 제작 가능합니다'
    },
    {
      icon: 'ri-recycle-line',
      title: '위생적',
      description: 'STS 소재로 녹이 슬지 않고 위생적입니다'
    },
    {
      icon: 'ri-tools-line',
      title: '유지보수 용이',
      description: '구조가 단순하여 관리가 쉽습니다'
    }
  ];

  const specifications = [
    { label: '재질', value: 'STS304 등' },
    { label: '용량 범위', value: '3톤 ~' },
    { label: '직경', value: '600mm ~' },
  ];

  const applications = [
    {
      icon: 'ri-building-line',
      title: '아파트 및 빌딩',
      description: '중소규모 건물의 급수 시스템'
    },
    {
      icon: 'ri-home-line',
      title: '단독주택',
      description: '가정용 저수 시설'
    },
    {
      icon: 'ri-hospital-line',
      title: '병원 및 학교',
      description: '공공시설 급수 시스템'
    },
    {
      icon: 'ri-store-line',
      title: '상업시설',
      description: '음식점, 호텔 등 상업용'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 text-white py-24">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <Link to="/process" className="text-teal-200 hover:text-white transition-colors cursor-pointer">
                  제작과정
                </Link>
                <i className="ri-arrow-right-s-line text-teal-200"></i>
                <span>STS원통형 물탱크</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight">
                STS원통형 물탱크
              </h1>
              <p className="text-xl md:text-2xl text-teal-50 max-w-3xl leading-relaxed">
                일체형 구조로 내구성이 뛰어납니다.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Product Image */}
        <section className="py-20 bg-white">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="relative w-full h-[600px] rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/8529c6d0c52b6845b3d7225ae30caaf1.jpeg"
                alt="STS원통형 물탱크"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
            </div>
          </div>
        </section>

        {/* Advantages Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                원통형 물탱크의 장점
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                STS원통형 물탱크가 가진 5가지 핵심 장점을 확인하세요
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {advantages.map((advantage, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className="w-16 h-16 bg-teal-600 rounded-xl flex items-center justify-center text-white text-3xl mb-6">
                    <i className={advantage.icon}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {advantage.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {advantage.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Steps */}
        <section className="py-20 bg-white">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                원통형 물탱크 제작 순서
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                체계적인 7단계 제작 프로세스로 최고 품질을 보장합니다
              </p>
            </div>

            <div className="space-y-8">
              {processSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-3xl overflow-hidden border border-teal-100 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                    <div className={`p-8 md:p-12 flex flex-col justify-center ${index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}`}>
                      <div className="flex items-center gap-6 mb-6">
                        <div className="text-6xl font-bold text-teal-600/20">
                          {step.step}
                        </div>
                        <div className="w-20 h-20 bg-teal-600 rounded-2xl flex items-center justify-center text-white text-3xl flex-shrink-0">
                          <i className={step.icon}></i>
                        </div>
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900 mb-4">
                        {step.title}
                      </h3>
                      <p className="text-lg text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                    <div className={`relative h-[400px] ${index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'}`}>
                      <img
                        src={step.image}
                        alt={step.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                제품 특징
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                STS원통형 물탱크의 주요 특징과 장점을 확인하세요
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className="w-16 h-16 bg-teal-600 rounded-xl flex items-center justify-center text-white text-3xl mb-6">
                    <i className={feature.icon}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Applications */}
        <section className="py-20 bg-white">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                적용 분야
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                다양한 시설에서 활용되는 STS원통형 물탱크
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {applications.map((app, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center text-white text-4xl mx-auto mb-6">
                    <i className={app.icon}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {app.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {app.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Specifications */}
        <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                제품 사양
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                STS원통형 물탱크의 상세 사양을 확인하세요
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200">
                {specifications.map((spec, index) => (
                  <div key={index} className="p-8 text-center hover:bg-gray-50 transition-colors duration-300">
                    <div className="text-sm font-semibold text-teal-600 mb-2 uppercase tracking-wide">
                      {spec.label}
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {spec.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 rounded-3xl p-12 md:p-16 text-center text-white shadow-2xl">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                STS원통형 물탱크 제작 상담
              </h2>
              <p className="text-xl text-teal-50 mb-8 max-w-2xl mx-auto leading-relaxed">
                전문 상담원이 고객님의 요구사항에 맞는 최적의 솔루션을 제안해드립니다
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-block bg-white text-teal-600 px-10 py-4 rounded-full font-bold text-lg hover:bg-teal-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  상담 문의하기
                </Link>
                <Link
                  to="/products"
                  className="inline-block bg-teal-500 text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-teal-400 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  다른 제품 보기
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default STSCylinderPage;